/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import makeStyles from '@material-ui/styles/makeStyles';
import Paper from '@material-ui/core/Paper';
import { useUID } from 'react-uid';
import { array, bool, func, string } from 'prop-types';
import TextField from '@material-ui/core/TextField';

const useStyles = (mobile, isFloraVerse) =>
    makeStyles(() => ({
        card: {
            color: '#000000 !important',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: '15px 20px',
            margin: '24px 0',
            boxShadow: 'none',
            borderRadius: '8px',
        },
        fieldSet: {
            border: 0,
            padding: 0,
            margin: 0,
            position: 'relative',
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            justifyContent: 'center',
        },
        titleContainer: {
            textAlign: 'center',
            fontWeight: 700,
            lineHeight: '26px',
            marginBottom: '25px !important',
            '& > p': {
                fontSize: `${mobile ? '18px' : '20px'} !important`,
            },
            '& > p:last-child': {
                marginBottom: '0',
                fontSize: '12px !important',
            },
        },
        radio: {
            fontSize: '16px !important',
            padding: '8px 20px',
            borderRadius: '30px',
            backgroundColor: '#F5F5F5',
            minWidth: '75px',
            cursor: 'pointer',
            fontWeight: 400,
            lineHeight: '20px',
            textAlign: 'center',
        },
        active: {
            backgroundColor: isFloraVerse?'#117B91': '#F55858',
            color: '#FFFFFF',
        },
        radioInput: {
            position: 'absolute !important',
            clip: 'rect(0,0,0,0)',
            pointerEvents: 'none',
        },
        cardContainer: {
            display: 'relative',
            width: '100%',
        },
        input: {
            height: '50px',
            width: '100%',
            marginBottom: '16px',
            backgroundColor: '#F5F5F5',
            border: '1px solid #E9E9E9',
            borderRadius: '4px',
        },
        hidden: {
            display: 'none',
        },
    }));

const CardWithButtonsMultiple = ({ title, mobile, userSelections, handleOptionChange, placeholder, subtitle, isFloraVerse}) => {
    const classesLayout = useStyles(mobile, isFloraVerse);
    const classes = classesLayout();

    const [selectedOptions, setSelectedOptions] = useState(userSelections.map(() => false));
    const [textInputValue, setTextInputValue] = useState('');

    const textInputDisabled = selectedOptions.filter((val) => val).length >= 3 && !textInputValue;

    const formLabel = useUID();

    const updateParent = (options, textValue) => {
        const activeSelections = options.reduce((acc, selected, index) => {
            if (selected) acc.push(userSelections[index]);
            return acc;
        }, []);

        if (textValue) activeSelections.push(textValue);
        handleOptionChange(activeSelections);
    };

    const handleRadioChange = (index) => {
        const totalSelections = selectedOptions.filter((val) => val).length + (textInputValue ? 1 : 0);

        if (!selectedOptions[index] && totalSelections >= 3) {
            return;
        }

        const updatedSelections = selectedOptions.map((selected, ind) => (ind === index ? !selected : selected));
        setSelectedOptions(updatedSelections);
        updateParent(updatedSelections, textInputValue);
    };

    const handleTextFieldChange = (e) => {
        const { value } = e.target;
        if (value && selectedOptions.filter((val) => val).length >= 3 && !textInputValue) return;
        setTextInputValue(value);
        updateParent(selectedOptions, value);
    };

    return (
        <Paper className={classes.card}>
            <div className={classes.titleContainer}>
                <p>{title}</p>
                {subtitle && <p>{subtitle}</p>}
            </div>
            <fieldset
                className={classes.fieldSet}
                aria-labelledby={`${formLabel}-radio-group`}
                name={`${formLabel}-controlled-radio-buttons-group`}
            >
                {userSelections.map((option, index) => (
                    <label
                        className={`${classes.radio} ${selectedOptions[index] ? classes.active : ''}`}
                        key={`button${useUID()}`}
                    >
                        {option}
                        <input
                            className={classes.radioInput}
                            type="radio"
                            aria-disabled={false}
                            name={`${formLabel}-controlled-radio-buttons-group`}
                            value={option}
                            onClick={(e) => handleRadioChange(index)}
                        />
                    </label>
                ))}
                <div className={`${classes.cardContainer} ${textInputDisabled ? classes.hidden : ''}`}>
                    <TextField
                        autoComplete="off"
                        className={classes.input}
                        value={textInputValue}
                        name="Other"
                        placeholder={placeholder}
                        onChange={(e) => handleTextFieldChange(e)}
                        disabled={textInputDisabled}
                        inputProps={{ style: { fontSize: '16px', color: '#000' } }}
                        InputProps={{
                            style: {
                                fontSize: '16px',
                                height: '50px',
                                width: '100%',
                                padding: '0 14px',
                            },
                            disableUnderline: true,
                        }}
                    />
                </div>
            </fieldset>
        </Paper>
    );
};

CardWithButtonsMultiple.defaultProps = {
    placeholder: '',
    subtitle: '',
};

CardWithButtonsMultiple.propTypes = {
    handleOptionChange: func.isRequired,
    title: string.isRequired,
    subtitle: string,
    userSelections: array.isRequired,
    placeholder: string,
    mobile: bool.isRequired,
};

export default CardWithButtonsMultiple;
